'use client'

import { Button } from '@brew/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@brew/ui/dialog'
import { Input } from '@brew/ui/input'
import { type FormEvent, type ReactNode, useCallback, useState } from 'react'
import { Spinner } from '@/components/ui/spinner'

export type ImageFromLinkDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  submitLabel: string
  pendingLabel: string
  /** Idle leading icon on the submit button; the spinner replaces it while pending. */
  submitIcon?: ReactNode
  isPending?: boolean
  onSubmit: (url: string) => Promise<void> | void
}

/**
 * Shared URL-import dialog used by brand assets and chat attachments.
 * Production wires the fetch/optimize action; the sandbox passes a no-op.
 * The draft URL clears on every close, including a caller closing the
 * dialog itself after a successful import.
 */
export function ImageFromLinkDialog({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  pendingLabel,
  submitIcon,
  isPending = false,
  onSubmit,
}: ImageFromLinkDialogProps) {
  const [url, setUrl] = useState('')
  const [wasOpen, setWasOpen] = useState(open)
  if (wasOpen !== open) {
    setWasOpen(open)
    if (!open) {
      setUrl('')
    }
  }

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (isPending) {
        return
      }
      onOpenChange(nextOpen)
    },
    [isPending, onOpenChange]
  )

  const actionLabel = isPending ? pendingLabel : submitLabel

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      const trimmed = url.trim()
      if (!trimmed || isPending) {
        return
      }
      await onSubmit(trimmed)
    },
    [isPending, onSubmit, url]
  )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <Input
            type="url"
            value={url}
            onChange={(event) => setUrl(event.currentTarget.value)}
            placeholder="https://example.com/image.png"
            autoFocus
            disabled={isPending}
          />
          <DialogFooter>
            <Button type="submit" disabled={!url.trim() || isPending}>
              {isPending ? (
                <Spinner className="size-3.5" />
              ) : (
                (submitIcon ?? null)
              )}
              {actionLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
